import { useState } from "react";
import { toast } from "sonner";
import { Download, Upload } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { qty, today } from "@/components/stores/storesUi";

const ENTRY_LABELS = {
  opening: "Opening Balance",
  receipt: "GOODS RECEIPT",
  issue: "GOODS ISSUE",
  return: "PRODUCTION RETURN",
  slit_out: "SLITTING (parent)",
  slit_in: "SLITTING (child)",
};

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export function ExportButton() {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const res = await api.get("/stores/export.xlsx", { responseType: "blob" });
      saveBlob(res.data, `STOCK REPORT AS ERP ${today()}.xlsx`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button data-testid="stores-export" variant="outline" className="rounded-sm gap-1.5" onClick={run} disabled={busy}>
      <Download size={13} /> {busy ? "Preparing…" : "Export Excel"}
    </Button>
  );
}

export function ImportButton({ onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button data-testid="stores-import-open" variant="outline" className="rounded-sm gap-1.5" onClick={() => setOpen(true)}>
        <Upload size={13} /> Import Excel
      </Button>
      {open && <ImportDialog onClose={() => setOpen(false)} onDone={() => { setOpen(false); onDone?.(); }} />}
    </>
  );
}

function ImportDialog({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [replace, setReplace] = useState(false);

  const send = async (commit) => {
    const form = new FormData();
    form.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/stores/import", form, { params: { commit, replace }, timeout: 10 * 60 * 1000 });
      if (commit) {
        const c = data.committed;
        const removed = c.removed ? ` · ${c.removed.toLocaleString("en-IN")} removed` : "";
        toast.success(
          `Imported ${c.written.toLocaleString("en-IN")} new entries · ${c.already_in_erp.toLocaleString("en-IN")} already in the ERP${removed}`,
        );
        onDone();
      } else {
        setCheck(data);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadExceptions = () => {
    const head = ["Sheet", "Row", "Severity", "Issue", "Detail"];
    const lines = [head, ...check.exceptions.map((x) => [x.sheet, x.row, x.severity, x.issue, x.detail])];
    const csv = lines.map((l) => l.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    saveBlob(new Blob([csv], { type: "text/csv" }), `stock-import-exceptions-${today()}.csv`);
  };

  const errors = check ? check.exception_counts.filter((x) => x.severity === "error").reduce((n, x) => n + x.count, 0) : 0;
  const r = check?.reconciliation;
  const p = check?.plan;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import stores workbook</DialogTitle>
          <DialogDescription>
            Same sheets and columns as the export: ITEM MASTER, SUPPLIER MASTER, Opening Balance, GOODS RECEIPT, GOODS ISSUE,
            PRODUCTION RETURN, SLITTING. Check the file first; nothing is written until you import.
          </DialogDescription>
        </DialogHeader>

        <input
          data-testid="stores-import-file"
          type="file"
          accept=".xlsx,.xlsm"
          onChange={(e) => { setFile(e.target.files?.[0] || null); setCheck(null); }}
          className="text-sm"
        />
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="accent-[#FF5A00] mt-0.5"
            checked={replace}
            onChange={(e) => { setReplace(e.target.checked); setCheck(null); }}
            data-testid="stores-import-replace"
          />
          <span>
            Replace previous imports of these sheets
            <span className="block text-xs text-neutral-500">
              Use for a full re-sync of the workbook. Off: rows are added and rows already imported are skipped. Entries made in the app are never touched.
            </span>
          </span>
        </label>

        {check && (
          <div className="space-y-3 text-sm" data-testid="stores-import-check">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="border border-neutral-200 p-2">
                <div className="label-caps text-[10px]">Items</div>
                <div className="font-mono-num font-bold">{check.items}</div>
                {check.items > 0 && <div className="text-[11px] text-neutral-500">{p.items_new} new</div>}
              </div>
              <div className="border border-neutral-200 p-2"><div className="label-caps text-[10px]">New rolls</div><div className="font-mono-num font-bold">{qty(check.rolls, 0)}</div></div>
              <div className="border border-neutral-200 p-2"><div className="label-caps text-[10px]">Stock report match</div><div className="font-mono-num font-bold">{r.items_checked ? `${r.items_matching}/${r.items_checked}` : "—"}</div></div>
              <div className="border border-neutral-200 p-2"><div className="label-caps text-[10px]">Lot-wise match</div><div className="font-mono-num font-bold">{r.rolls_checked ? `${r.rolls_matching}/${r.rolls_checked}` : "—"}</div></div>
            </div>
            <table className="w-full text-sm border border-neutral-200">
              <thead className="bg-neutral-50 label-caps text-[10px]">
                <tr>
                  <th className="text-left px-3 py-2">Sheet</th>
                  <th className="text-right px-3 py-2">Rows in file</th>
                  <th className="text-right px-3 py-2">New</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(check.entries).map(([t, n]) => (
                  <tr key={t} className="border-t border-neutral-100">
                    <td className="px-3 py-1.5">{ENTRY_LABELS[t] || t}</td>
                    <td className="px-3 py-1.5 text-right font-mono-num">{qty(n, 0)}</td>
                    <td className="px-3 py-1.5 text-right font-mono-num font-semibold">{qty(p.new_entries[t] || 0, 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-neutral-600" data-testid="stores-import-plan">
              {qty(p.already_in_erp, 0)} row(s) already in the ERP will be skipped.
              {replace && ` ${qty(p.to_remove, 0)} row(s) imported earlier and no longer in this file will be removed.`}
            </p>
            {check.exception_counts.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="label-caps text-[10px]">Exceptions ({check.exception_total})</div>
                  <button onClick={downloadExceptions} className="text-xs text-[#FF5A00] hover:underline">Download list (CSV)</button>
                </div>
                <table className="w-full text-xs border border-neutral-200">
                  <tbody>
                    {check.exception_counts.map((x) => (
                      <tr key={`${x.sheet}-${x.issue}`} className="border-t border-neutral-100">
                        <td className="px-3 py-1.5 font-mono-num text-right w-14">{x.count}</td>
                        <td className={`px-2 py-1.5 uppercase tracking-widest text-[10px] ${x.severity === "error" ? "text-rose-700" : x.severity === "warning" ? "text-amber-700" : "text-neutral-500"}`}>{x.severity}</td>
                        <td className="px-2 py-1.5">{x.sheet}: {x.issue}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[11px] text-neutral-500 mt-1">
                  error = row skipped · warning = counted for the item but not against a roll · check = the sheet itself disagrees with its rows
                </p>
              </div>
            )}
            {errors > 0 && <p className="text-xs text-rose-700">{errors} row(s) will be skipped. Fix them in the workbook and import again, or continue without them.</p>}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {!check ? (
            <Button data-testid="stores-import-check-btn" onClick={() => send(false)} disabled={!file || busy} className="bg-[#FF5A00] hover:bg-[#e04f00]">
              {busy ? "Checking…" : "Check file"}
            </Button>
          ) : (
            <Button data-testid="stores-import-commit" onClick={() => send(true)} disabled={busy} className="bg-[#FF5A00] hover:bg-[#e04f00]">
              {busy ? "Importing…" : "Import"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
